import openai from './openaiClient';
import {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Streams a chat completion response chunk by chunk.
 * @param {Array} messages - Array of message objects with role and content.
 * @param {Function} onChunk - Callback to handle each streamed chunk.
 * @param {Function} onError - Callback to handle errors.
 * @param {string} systemContext - Optional system context to inject into the system message.
 */
export async function streamChatCompletion(messages, onChunk, onError, systemContext = null) {
  try {
    // If systemContext is provided, inject it into the messages
    const messagesWithContext = systemContext
      ? [
          {
            role: 'system',
            content: systemContext,
          },
          ...messages?.filter(m => m?.role !== 'system'), // Remove any existing system messages
        ]
      : messages;

    const stream = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: messagesWithContext,
      stream: true,
      max_tokens: 800, // Reduced from 2000 to limit response length
    });

    for await (const chunk of stream) {
      const content = chunk?.choices?.[0]?.delta?.content || '';
      if (content) {
        onChunk(content);
      }
    }
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in streaming chat completion:', error);
    }
    if (onError) {
      onError(errorInfo?.message);
    }
  }
}

export default {
  streamChatCompletion,
};